import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/data/api";
import { authKeys, logout } from "@/data/auth";
import {
  acceptInvite,
  getInviteMetadata,
  redeemInviteSession,
  workspaceKeys,
} from "@/data/workspaces";
import { Button } from "@/ui/brand-button";

interface PropsIface {
  token: string | null;
  onAccepted: (slug: string) => void;
  onPicker: () => void;
  onLogin: (email: string | null) => void;
}

type InviteState =
  | { kind: "loading" }
  | {
      kind: "auth";
      workspaceName: string | null;
      inviteEmail: string;
    }
  | {
      kind: "email_mismatch";
      workspaceName: string | null;
      inviteEmail: string;
      currentEmail: string;
    }
  | { kind: "login_fallback"; inviteEmail: string | null }
  | { kind: "accepting"; workspaceName: string | null }
  | { kind: "accepted"; slug: string }
  | { kind: "expired" }
  | { kind: "invalid" }
  | { kind: "missing_token" }
  | { kind: "error"; message: string };

interface InviteMetadataLike {
  requires_auth?: boolean;
  email_mismatch?: boolean;
  workspace?: { slug: string | null; name: string | null } | null;
  invite_email?: string;
  current_email?: string;
  error?: string;
}

// Maps a failed GET /invite/:token into a renderable state. Exported for
// tests: this is the only branching logic in the screen that is easy to get
// subtly wrong (status-code + body-flag combinations).
export function stateFromInviteError(err: unknown): InviteState {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as InviteMetadataLike;
    if (err.status === 404) return { kind: "invalid" };
    if (err.status === 410) return { kind: "expired" };
    if (err.status === 401 && body.requires_auth) {
      return {
        kind: "auth",
        workspaceName: body.workspace?.name ?? null,
        inviteEmail: body.invite_email ?? "",
      };
    }
    if (err.status === 409 && body.email_mismatch) {
      return {
        kind: "email_mismatch",
        workspaceName: body.workspace?.name ?? null,
        inviteEmail: body.invite_email ?? "",
        currentEmail: body.current_email ?? "",
      };
    }
    return { kind: "error", message: body.error ?? err.message };
  }
  return {
    kind: "error",
    message: err instanceof Error ? err.message : String(err),
  };
}

// Maps a failed POST /invite/:token/session into a renderable state. A 410
// with `already_accepted` means the invitation was consumed, so the link no
// longer works as a sign-in credential — the invitee signs in normally
// instead (the emailed code flow on /login), which still gets them into the
// workspace. Plain 410s (revoked/expired) render as expired like resolve.
export function stateFromSessionError(
  err: unknown,
  inviteEmail: string | null,
): InviteState {
  if (err instanceof ApiError && err.status === 410) {
    const body = (err.body ?? {}) as InviteMetadataLike & {
      already_accepted?: boolean;
    };
    if (body.already_accepted) {
      return {
        kind: "login_fallback",
        inviteEmail: body.invite_email ?? inviteEmail,
      };
    }
  }
  return stateFromInviteError(err);
}

// /invite acceptance page. One emailed link does the whole journey: resolve
// the token, then either auto-accept (already signed in as the invited email)
// or redeem the link for a session server-side on the explicit Accept click —
// no second email, no code entry. Nothing happens on page load beyond the
// read-only resolve, so email-scanner prefetches can't consume the invite.
export function InviteScreen({
  token,
  onAccepted,
  onPicker,
  onLogin,
}: PropsIface) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<InviteState>(
    token ? { kind: "loading" } : { kind: "missing_token" },
  );
  const [busy, setBusy] = useState(false);
  const startedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token || startedRef.current === token) return;
    startedRef.current = token;
    void resolveInvite(token);
    // resolveInvite closes over local setters only; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function resolveInvite(t: string) {
    setState({ kind: "loading" });
    try {
      const body = await getInviteMetadata(t);
      // Signed in as the invited email: the emailed link plus an authenticated
      // matching session is all the intent we need — accept immediately.
      await acceptResolved(t, body.workspace?.name ?? null);
    } catch (err) {
      setState(stateFromInviteError(err));
    }
  }

  async function acceptResolved(t: string, workspaceName: string | null) {
    setState({ kind: "accepting", workspaceName });
    try {
      const body = await acceptInvite(t);
      await finishAccept(body.workspace?.slug ?? null);
    } catch (err) {
      setState(stateFromInviteError(err));
    }
  }

  // The one-click path: the emailed link is the credential. The backend mints
  // a session for the invited email and accepts in a single POST.
  async function redeemAndAccept(
    inviteEmail: string,
    workspaceName: string | null,
  ) {
    if (!token) return;
    setBusy(true);
    setState({ kind: "accepting", workspaceName });
    try {
      const body = await redeemInviteSession(token);
      // The session cookie changed under the router's feet; drop any cached
      // session so route guards re-fetch instead of trusting stale state.
      queryClient.removeQueries({ queryKey: authKeys.session() });
      await finishAccept(body.workspace?.slug ?? null);
    } catch (err) {
      setState(stateFromSessionError(err, inviteEmail));
    } finally {
      setBusy(false);
    }
  }

  async function finishAccept(slug: string | null) {
    if (!slug) {
      setState({ kind: "error", message: "No workspace returned" });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: workspaceKeys.me() });
    setState({ kind: "accepted", slug });
    onAccepted(slug);
  }

  async function switchAccount(
    inviteEmail: string,
    workspaceName: string | null,
  ) {
    setBusy(true);
    try {
      await logout();
    } catch {
      // Continue anyway; the session redemption mints a fresh session.
    }
    queryClient.removeQueries({ queryKey: authKeys.session() });
    await redeemAndAccept(inviteEmail, workspaceName);
  }

  if (state.kind === "loading") {
    return <InviteCard title="Checking invitation" body="One moment." busy />;
  }
  if (state.kind === "missing_token") {
    return (
      <InviteCard
        title="No invitation link"
        body="Open the invitation link from your email."
        actions={
          <Button type="button" variant="outline" onClick={onPicker}>
            Go home
          </Button>
        }
      />
    );
  }
  if (state.kind === "expired") {
    return (
      <InviteCard
        title="Invitation expired"
        body="Ask the person who invited you to send a new link."
      />
    );
  }
  if (state.kind === "invalid") {
    return (
      <InviteCard
        title="Invalid invitation"
        body="This invite link is no longer available."
      />
    );
  }
  if (state.kind === "auth") {
    const workspace = workspaceNameForDisplay(state.workspaceName);
    return (
      <InviteCard
        title={`Join ${workspace}`}
        body={`You've been invited as ${state.inviteEmail}. Accepting signs you in and takes you straight to the workspace.`}
        actions={
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={busy}
            data-testid="invite-accept"
            onClick={() =>
              void redeemAndAccept(state.inviteEmail, state.workspaceName)
            }
          >
            {busy ? "Joining..." : "Accept invitation"}
          </Button>
        }
      />
    );
  }
  if (state.kind === "login_fallback") {
    return (
      <InviteCard
        title="Invitation already used"
        body={
          state.inviteEmail
            ? `This link has already been used to join. Sign in as ${state.inviteEmail} to continue.`
            : "This link has already been used to join. Sign in to continue."
        }
        actions={
          <Button
            type="button"
            size="lg"
            className="w-full"
            data-testid="invite-login-fallback"
            onClick={() => onLogin(state.inviteEmail)}
          >
            Go to sign in
          </Button>
        }
      />
    );
  }
  if (state.kind === "email_mismatch") {
    return (
      <InviteCard
        title="Use the invited email"
        body={`This invitation to ${workspaceNameForDisplay(
          state.workspaceName,
        )} is for ${state.inviteEmail}, but you're signed in as ${state.currentEmail}.`}
        actions={
          <div className="flex w-full flex-col gap-2">
            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={busy}
              onClick={() =>
                void switchAccount(state.inviteEmail, state.workspaceName)
              }
            >
              Continue as {state.inviteEmail}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onPicker}
            >
              Cancel
            </Button>
          </div>
        }
      />
    );
  }
  if (state.kind === "accepting") {
    return (
      <InviteCard
        title={`Joining ${workspaceNameForDisplay(state.workspaceName)}`}
        body="Setting up your access."
        busy
      />
    );
  }
  if (state.kind === "accepted") {
    return (
      <InviteCard title="Invitation accepted" body="Taking you in." busy />
    );
  }
  return (
    <InviteCard title="Could not process invitation" body={state.message} />
  );
}

function workspaceNameForDisplay(name: string | null): string {
  return name?.trim() || "this workspace";
}

function InviteCard({
  title,
  body,
  busy = false,
  message,
  actions,
  children,
}: {
  title: string;
  body: string;
  busy?: boolean;
  message?: string | null;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="anim-fade-in flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md space-y-8 rounded-[3px] border border-wiki-border bg-wiki-bg-2/80 p-9 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.6)]">
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt=""
              width={28}
              height={28}
              className="rounded-[6px] invert"
              draggable={false}
            />
            <span className="text-[15px] font-semibold tracking-tight text-wiki-fg-1">
              Parable
            </span>
          </div>
          <h1 className="text-3xl leading-[1.15] font-semibold tracking-tight text-wiki-fg-1">
            {busy ? `${title}…` : title}
          </h1>
          <p className="text-[14px] leading-relaxed text-wiki-fg-2">{body}</p>
        </div>
        {children}
        {actions}
        {message ? (
          <div
            className="rounded-[3px] border border-red-900/40 bg-red-950/30 px-3 py-2 text-sm text-red-200"
            role="alert"
          >
            {message}
          </div>
        ) : null}
      </div>
    </div>
  );
}
